import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button as FPButton } from '@finpay-development/shared-components';
import {
    Box,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Button,
    Link
} from '@mui/material';
import { VOBSelector } from './sections/VOBSelector';
import { VOBFormFields } from './sections/VOBFormFields';
import { VOBFormData } from './types';
import { LevelsOfCare } from './sections/LevelsOfCare';
import { NetworkTypeSelector } from './sections/NetworkTypeSelector';
import { RateAsSelector } from './sections/RateAsSelector';
import { AvailityErrorDialog } from './sections/AvailityErrorDialog';
import { DatePicker } from '@finpay-development/shared-components';
import { useVOBForm } from './hooks/useVOBForm';
import { showErrorStatus, showStatus } from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import { downloadVobPdf } from '@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-thunk';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PhoneIcon from '@mui/icons-material/Phone';
import { AppDispatch } from '../../../../shared/state/store';
import AccessControl from '@finpay-development/shared-platform/lib/esm/security/components/access-control';
import { RolePageNames } from '@finpay-development/shared-platform/lib/esm/security/model/role-page-names';
import { clearSelectedEstimate, estimatorSlice } from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-slice';
import { RootState } from 'src/shared/state/root-reducer';
import { saveEstimateAfterVob } from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-thunk';
import { loadAndSetRateAsPayorPlan, rematchRateAsPayorPlanToFacility, isFacilityPlanIdOnFacility } from '../../../../estimates/utils/payor-plan-utils';
import { Client, Estimate, Facility, Payor, PayorPlan, Plan, Vob } from '@finpay-estimation/estimation-types';
import { Utils } from '@finpay-development/shared-platform/lib/esm/shared/utils';
import {
    ACTIVE_POLICY_EFFECTIVE_TO_ERROR_MESSAGE,
    isActivePolicyEffectiveToBeforeToday,
    VOB_SAVE_VALIDATION_ISSUES_PREFIX
} from './utils/active-policy-effective-to-validation';
import { syncIocFacilityAfterVobSave } from './utils/sync-ioc-facility';
import { resolveDefaultFacilityId } from './utils/vob-utils';
import type { Employers } from '@finpay-development/shared-platform/lib/esm/meta-data/models/metaData';
import { getEmployers } from '@finpay-development/shared-platform/lib/esm/meta-data/state/meta-data-thunk';

function trimmedEmployerField(value: unknown): string {
    if (typeof value !== 'string') return '';
    return value.trim();
}

/** Patient / CRM payloads may use camelCase or snake_case for nested income. */
function employerNameFromContact(contact: unknown): string {
    if (!contact || typeof contact !== 'object') return '';
    const c = contact as {
        contactIncome?: { employer?: unknown };
        contact_income?: { employer?: unknown };
    };
    return (
        trimmedEmployerField(c.contactIncome?.employer) ||
        trimmedEmployerField(c.contact_income?.employer)
    );
}

/** Estimate Patient tab employer dropdown uses clientEmployerId + metaData.employers[clientId], not always contactIncome.employer text. */
function clientEmployerIdFromContact(contact: unknown): number | null {
    if (!contact || typeof contact !== 'object') return null;
    const c = contact as {
        contactIncome?: { clientEmployerId?: unknown };
        contact_income?: { client_employer_id?: unknown };
    };
    const raw = c.contactIncome?.clientEmployerId ?? c.contact_income?.client_employer_id;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
}

function employerNameFromClientEmployersMap(
    clientId: number | string | undefined,
    clientEmployerId: number | null,
    employersByClient: Employers
): string {
    if (clientEmployerId == null || clientId == null || clientId === '') return '';
    const cid = Number(clientId);
    if (!Number.isFinite(cid)) return '';
    const list = employersByClient[cid];
    if (!list?.length) return '';
    const match = list.find((e) => Number(e.clientEmployerId) === Number(clientEmployerId));
    return trimmedEmployerField(match?.employerName);
}

function VOBForm({
    onAvailityRunningChange,
}: {
    onAvailityRunningChange?: (isRunning: boolean) => void;
} = {}) {
    const dispatch = useDispatch<AppDispatch>();
    const vobHeaderActionsRef = React.useRef<HTMLDivElement>(null);
    const [headerActionsWidthPx, setHeaderActionsWidthPx] = React.useState(0);
    const [isSaving, setIsSaving] = React.useState(false);
    const [resetButtonStates, setResetButtonStates] = React.useState(false);
    const [buttonStates, setButtonStates] = React.useState<{
        isDeductibleButtonPressed: boolean;
        isOopmButtonPressed: boolean;
        isServiceLimitsButtonPressed: boolean;
        isMaxDaysButtonPressed: boolean;
        isCoinsuranceButtonPressed: boolean;
    } | undefined>(undefined);
    const [dateErrors, setDateErrors] = React.useState({
        effectiveFrom: false,
        effectiveTo: false
    });
    const [facilityError, setFacilityError] = React.useState(false);
    const [deductibleError, setDeductibleError] = React.useState(false);
    const [remainingError, setRemainingError] = React.useState(false);
    const [oopmError, setOopmError] = React.useState(false);
    const [oopmRemainingError, setOopmRemainingError] = React.useState(false);
    const [deductibleErrorMessage, setDeductibleErrorMessage] = React.useState('');
    const [remainingErrorMessage, setRemainingErrorMessage] = React.useState('');
    const [oopmErrorMessage, setOopmErrorMessage] = React.useState('');
    const [oopmRemainingErrorMessage, setOopmRemainingErrorMessage] = React.useState('');
    const [copayError, setCopayError] = React.useState(false);
    const [maxDaysError, setMaxDaysError] = React.useState(false);
    const [coinsuranceError, setCoinsuranceError] = React.useState(false);
    const [copayErrorMessage, setCopayErrorMessage] = React.useState('');
    const [maxDaysErrorMessage, setMaxDaysErrorMessage] = React.useState('');
    const [coinsuranceErrorMessage, setCoinsuranceErrorMessage] = React.useState('');

    const [levelErrors, setLevelErrors] = React.useState<Map<number, {
        deductibleTotalError?: boolean;
        deductibleRemainingError?: boolean;
        oopmTotalError?: boolean;
        oopmRemainingError?: boolean;
        copayError?: boolean;
        maxDaysError?: boolean;
        coinsuranceError?: boolean;
        deductibleTotalErrorMessage?: string;
        deductibleRemainingErrorMessage?: string;
        oopmTotalErrorMessage?: string;
        oopmRemainingErrorMessage?: string;
        copayErrorMessage?: string;
        maxDaysErrorMessage?: string;
        coinsuranceErrorMessage?: string;
    }>>(new Map());

    const levelsOfCare = useSelector(
        (state: RootState) => state.estimatorContext.facilityLevelsOfCareList
    );

    /** Dropdown display value only; Redux rateAsPayorPlan is not updated */
    const [selectedRateAsFacilityPlanId, setSelectedRateAsFacilityPlanId] = React.useState<number | null>(null);

    const estimate = useSelector(
        (state: RootState) => state.estimatorContext.selectedEstimate
    );
    const selectedEncounter = useSelector(
        (state: RootState) => state.patientContext.selectedEncounter
    );
    const isSelfPay = useSelector(
        (state: RootState) => state.estimatorContext.selectedEstimate?.isSelfPay || false
    );
    const isPrivatePay = useSelector(
        (state: RootState) => state.estimatorContext.selectedEstimate?.isPrivatePay || false
    );
    const reduxRateAsPayorMap = useSelector(
        (state: RootState) => state.estimatorContext.rateAsPayorMap
    ) || {};
    const reduxMasterPayors = useSelector(
        (state: RootState) => state.adminContext.adminConfigurationContext?.payersMap
    ) || new Map();
    const inboundPtRecord = useSelector((state: RootState) => state.patientContext.inboundPtRecord);
    const employersByClient = useSelector((state: RootState) => state.metaData.employers);
    const {
        // States
        activePolicy,
        carryover,
        selectedVOB,
        memberId,
        facilities,
        selectedFacility,
        facilitiesLoading,
        networkType,
        memberType,
        liveVOB,
        formData,
        currentVob,
        isRunningCRMVOB,
        isRunningAvailityVOB,
        isLoadingRunAvailityVOBButton,
        effectiveFromDate,
        effectiveToDate,
        facilityPayersState,
        rateAsPayorPlan,
        isPayerMatched,
        comparisonError,
        selectedPatient,
        selectedPatientCurrentInsurance,
        payersMap,
        estimateId,
        savedLOCDataMap,
        savedFormDataMap,
        showAvailityErrorDialog,
        availityErrorMessage,
        // Handlers
        handleInputChange,
        handleVOBSelectChange,
        handleNetworkTypeChange,
        handleMemberTypeChange,
        handleEffectiveFromDateChange,
        handleEffectiveToDateChange,
        handleFacilityChange,
        setSelectedFacility,
        setActivePolicy,
        setCarryover,
        setLiveVOB,
        runCRMVob,
        runAvailityVob,
        runAvailityVobWithOverrides,
        handleSaveVOB: saveVOB,
        setSavedFormDataMap,
        setSavedLOCDataMap,
        setShowAvailityErrorDialog,
    } = useVOBForm(
        () => setResetButtonStates(true),
        setButtonStates,
        (facilityPayersStateArg, payersMapArg, rateAsPayorPlanArg) => {
            if (selectedRateAsFacilityPlanId != null && facilityPayersStateArg?.length) {
                const payer = facilityPayersStateArg.find((p: any) =>
                    p.facilityPayorPlans?.some((pl: { facilityPayorPlanId: number }) => Number(pl.facilityPayorPlanId) === Number(selectedRateAsFacilityPlanId))
                );
                const plan = payer?.facilityPayorPlans?.find((pl: { facilityPayorPlanId: number }) => Number(pl.facilityPayorPlanId) === Number(selectedRateAsFacilityPlanId));
                if (payer && plan) {
                    const payorName = payer.facilityPayorName || payersMapArg?.get(payer.cfgPayorId)?.payorName;
                    const planName = plan.facilityPayorPlanName || payersMapArg?.get(payer.cfgPayorId)?.payorPlans?.find((p: any) => Number(p.payorPlanId) === Number(plan.cfgPayorPlanId))?.planName;
                    return {
                        payor: { payorId: payer.cfgPayorId, facilityPayorName: payer.facilityPayorName, payorName, inNetwork: payer.inNetwork },
                        plan: { payorPlanId: plan.cfgPayorPlanId, facilityPlanId: plan.facilityPayorPlanId, facilityPlanName: plan.facilityPayorPlanName, planName },
                        isOverridden: true,
                    } as PayorPlan;
                }
            }
            if (rateAsPayorPlanArg) {
                const vobRateAs = (currentVob as Vob & { rateAsPayorPlan?: PayorPlan })?.rateAsPayorPlan;
                const isUserOverride =
                    selectedRateAsFacilityPlanId != null ||
                    rateAsPayorPlanArg.isOverridden === true ||
                    vobRateAs?.isOverridden === true;
                const facilityPlanId = rateAsPayorPlanArg.plan?.facilityPlanId;
                if (
                    facilityPayersStateArg?.length &&
                    facilityPlanId != null &&
                    !isFacilityPlanIdOnFacility(facilityPayersStateArg, facilityPlanId)
                ) {
                    const rematched = rematchRateAsPayorPlanToFacility(
                        rateAsPayorPlanArg,
                        facilityPayersStateArg,
                        payersMapArg
                    );
                    if (rematched) {
                        return {
                            ...rematched,
                            isOverridden: isUserOverride || rematched.isOverridden === true,
                        } as PayorPlan;
                    }
                }
                return {
                    ...rateAsPayorPlanArg,
                    isOverridden: isUserOverride,
                } as PayorPlan;
            }
            return undefined;
        }
    );

    React.useEffect(() => {
        onAvailityRunningChange?.(isRunningAvailityVOB);
    }, [isRunningAvailityVOB, onAvailityRunningChange]);

    const employerResolutionClientId = React.useMemo(() => {
        const raw =
            selectedPatient?.clientId ??
            currentVob?.client?.clientId ??
            inboundPtRecord?.clientId;
        if (raw == null || raw === '') return NaN;
        const n = Number(raw);
        return Number.isFinite(n) ? n : NaN;
    }, [selectedPatient?.clientId, currentVob?.client?.clientId, inboundPtRecord?.clientId]);

    const employersForClient =
        Number.isFinite(employerResolutionClientId) && employerResolutionClientId > 0
            ? employersByClient[employerResolutionClientId]
            : undefined;

    React.useEffect(() => {
        if (!Number.isFinite(employerResolutionClientId) || employerResolutionClientId <= 0) return;
        if (employersForClient !== undefined) return;
        void dispatch(getEmployers(employerResolutionClientId));
    }, [dispatch, employerResolutionClientId, employersForClient]);

    const navigate = useNavigate();

    const vobGroupNameLabel = currentVob?.groupName?.trim() ?? '';
    const vobGroupNumLabel =
        currentVob?.groupNum?.trim() ||
        selectedPatientCurrentInsurance?.groupNumber?.trim() ||
        '';

    const planSponsorDisplayLine = (() => {
        const ps = currentVob?.planSponsor;
        if (!ps) return '';
        const sponsorName = ps.planSponsorName?.trim() ?? '';
        const sponsorId = ps.planSponsorId?.trim() ?? '';
        if (sponsorName && sponsorId) return `Sponsor: ${sponsorName}, ${sponsorId}`;
        if (sponsorName) return `Sponsor: ${sponsorName}`;
        if (sponsorId) return `Sponsor: ${sponsorId}`;
        return '';
    })();

    const vobGroupDisplayLine =
        vobGroupNameLabel && vobGroupNumLabel
            ? `Group: ${vobGroupNameLabel}, ${vobGroupNumLabel}`
            : vobGroupNameLabel
              ? `Group: ${vobGroupNameLabel}`
              : vobGroupNumLabel
                ? `Group: ${vobGroupNumLabel}`
                : '';

    const employerDisplayLine = (() => {
        const vobName = currentVob?.employer?.employerName?.trim() ?? '';
        const patientEmployerName =
            employerNameFromContact(selectedPatient?.contact) ||
            employerNameFromContact(inboundPtRecord?.patient?.contact);
        const clientEmployerId =
            clientEmployerIdFromContact(selectedPatient?.contact) ||
            clientEmployerIdFromContact(inboundPtRecord?.patient?.contact);
        const clientIdForEmployerLookup =
            selectedPatient?.clientId ?? currentVob?.client?.clientId ?? inboundPtRecord?.clientId;
        const fromClientEmployersCatalog = employerNameFromClientEmployersMap(
            clientIdForEmployerLookup,
            clientEmployerId,
            employersByClient
        );
        const inboundVobEmployerName = trimmedEmployerField(
            (inboundPtRecord as { vob?: { employer?: { employerName?: unknown } } })?.vob?.employer
                ?.employerName
        );
        const displayName =
            vobName ||
            patientEmployerName ||
            fromClientEmployersCatalog ||
            inboundVobEmployerName;
        if (!displayName) return '';
        return `Employer: ${displayName}`;
    })();

    const subscriberFirst = currentVob?.subscriber?.firstName?.trim() ?? '';
    const subscriberLast = currentVob?.subscriber?.lastName?.trim() ?? '';
    const subscriberRelationshipLabel = currentVob?.patient?.subscriberRelationship?.trim() ?? '';
    const subscriberDisplayLine =
        subscriberFirst && subscriberLast
            ? subscriberRelationshipLabel
                ? `Subscriber: ${subscriberFirst} ${subscriberLast}, ${subscriberRelationshipLabel}`
                : `Subscriber: ${subscriberFirst} ${subscriberLast}`
            : '';

    const payerLineNetworkIdSuffix = (() => {
        const planNetworkId = currentVob?.plan?.planNetworkId?.trim();
        if (planNetworkId) return ` Network ID: ${planNetworkId}`;
        return '';
    })();

    const payerPhoneDisplay = (() => {
        const phone = currentVob?.payor?.phone?.trim();
        if (phone) return Utils.formatPhoneNumber(phone);
        return '';
    })();

    const payerPortalUrl = (() => {
        const raw = currentVob?.payor?.url?.trim();
        if (!raw) return '';
        return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    })();

    const payerNameText = (() => {
        if (currentVob?.payor?.payorId) {
            return payersMap?.get(currentVob.payor.payorId)?.payorName || currentVob.payor.payorName || '';
        }
        if (selectedPatientCurrentInsurance?.payorId && payersMap?.get(selectedPatientCurrentInsurance.payorId)?.payorName) {
            return payersMap.get(selectedPatientCurrentInsurance.payorId)?.payorName || '';
        }
        return '';
    })();

    const payerPlanText = (() => {
        if (currentVob?.payor?.payorId) {
            const planName = currentVob.plan?.payorPlanId &&
                payersMap?.get(currentVob.payor.payorId)?.payorPlans?.find(
                    plan => Number(plan.payorPlanId) === Number(currentVob.plan?.payorPlanId)
                )?.planName;
            if (planName) return `/${planName}`;
            if (currentVob.plan?.planName) return `/${currentVob.plan.planName}`;
            return '';
        }
        if (selectedPatientCurrentInsurance?.payorId && payersMap?.get(selectedPatientCurrentInsurance.payorId)?.payorName) {
            const planName = selectedPatientCurrentInsurance?.payorPlanId &&
                payersMap?.get(selectedPatientCurrentInsurance.payorId)?.payorPlans?.find(
                    plan => Number(plan.payorPlanId) === selectedPatientCurrentInsurance.payorPlanId
                )?.planName;
            if (planName) return `/${planName}`;
            return '';
        }
        return '';
    })();

    const payerLineInsuranceSuffix = (() => {
        const type = currentVob?.insuranceType?.trim();
        const code = currentVob?.insuranceTypeCode?.trim();
        if (type) return ` — ${type}`;
        if (code) return ` — ${code}`;
        return '';
    })();

    /** On Rate as dropdown change: sync tab (IN/OUT), then load facility payers and set rateAsPayorPlan in Redux so Estimate page has latest data */
    const handleRateAsPayorPlanChangeTabOnly = React.useCallback((facilityPlanId: number) => {
        setSelectedRateAsFacilityPlanId(facilityPlanId);
        const payer = facilityPayersState?.find((p) =>
            p.facilityPayorPlans?.some((pl: { facilityPayorPlanId: number }) => Number(pl.facilityPayorPlanId) === Number(facilityPlanId))
        );
        const plan = payer?.facilityPayorPlans?.find((pl: { facilityPayorPlanId: number }) => Number(pl.facilityPayorPlanId) === Number(facilityPlanId));
        if (payer != null) {
            const newNetworkType = payer.inNetwork ? 'in' : 'out';
            if (newNetworkType !== networkType) {
                handleNetworkTypeChange(newNetworkType);
            }
        }
        if (payer && plan) {
            const payorObj: Payor = {
                payorId: payer.cfgPayorId,
                facilityPayorName: payer.facilityPayorName,
                payorName: payer.facilityPayorName || reduxMasterPayors?.get(payer.cfgPayorId)?.payorName,
                inNetwork: payer.inNetwork
            } as Payor;
            const planObj: Plan = {
                payorPlanId: plan.cfgPayorPlanId,
                facilityPlanId: plan.facilityPayorPlanId,
                facilityPlanName: plan.facilityPayorPlanName,
                planName: plan.facilityPayorPlanName || reduxMasterPayors?.get(payer.cfgPayorId)?.payorPlans?.find((p: { payorPlanId: number }) => Number(p.payorPlanId) === Number(plan.cfgPayorPlanId))?.planName
            } as Plan;
            const clientObj: Client = currentVob?.client ?? { clientId: Number(selectedPatient.clientId) } as Client;
            const facilityObj = currentVob?.facility ?? (selectedFacility ? { facilityId: Number(selectedFacility) } as Facility : undefined);
            if (facilityObj?.facilityId) {
                // Do not pass selectedVob so findAndSetRateAsPayorPlan uses the new dropdown selection
                // (otherwise it would use selectedVob.rateAsPayorPlan when isOverridden and ignore the change)
                loadAndSetRateAsPayorPlan(
                    clientObj,
                    facilityObj,
                    dispatch,
                    reduxMasterPayors,
                    payorObj,
                    planObj,
                    reduxRateAsPayorMap,
                    estimate ?? undefined,
                    rateAsPayorPlan ?? undefined,
                    undefined,
                    undefined,
                    currentVob?.vobId,
                    true
                )
                    .then(() => {
                        dispatch(
                            estimatorSlice.actions.setRateAsPayorPlan({
                                payor: payorObj,
                                plan: planObj,
                                isOverridden: true,
                            } as PayorPlan)
                        );
                    })
                    .catch((err) => {
                        console.error('loadAndSetRateAsPayorPlan on Rate as change:', err);
                    });
            }
        }
    }, [facilityPayersState, networkType, handleNetworkTypeChange, reduxMasterPayors, reduxRateAsPayorMap, dispatch, currentVob, selectedPatient.clientId, selectedFacility, estimate, rateAsPayorPlan]);

    React.useEffect(() => {
        setSelectedRateAsFacilityPlanId(null);
    }, [selectedFacility, currentVob?.vobId]);

    // Prefer rateAsPayorPlan from current VOB (API or vob.payor + vob.plan) for dropdown display
    const vobRateAsPayorPlan = React.useMemo(() => {
        if (!currentVob) return null;
        const fromVob = (currentVob as Vob & { rateAsPayorPlan?: { payor: Payor; plan: Plan | null } })?.rateAsPayorPlan;
        if (fromVob?.payor && fromVob?.plan != null) return { payor: fromVob.payor, plan: fromVob.plan };
        if (currentVob.payor && currentVob.plan) return { payor: currentVob.payor as Payor, plan: currentVob.plan as Plan };
        return null;
    }, [currentVob]);

    React.useEffect(() => {
        if (resetButtonStates) {
            // Reset the flag after the next render cycle
            const timer = setTimeout(() => {
                setResetButtonStates(false);
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [resetButtonStates]);

    React.useLayoutEffect(() => {
        const el = vobHeaderActionsRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return;
        }
        const update = () => setHeaderActionsWidthPx(el.offsetWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [isSaving]);

    const handleSaveVOB = async () => {
        setIsSaving(true);
        try {
            let hasError = false;
            let errorMessage = VOB_SAVE_VALIDATION_ISSUES_PREFIX;
            const newDateErrors = {
                effectiveFrom: false,
                effectiveTo: false
            };
            
            if (!selectedFacility) {
                hasError = true;
                setFacilityError(true);
                errorMessage += 'Facility is required; ';
            } else {
                setFacilityError(false);
            }
            
            if (!effectiveFromDate) {
                hasError = true;
                newDateErrors.effectiveFrom = true;
                errorMessage += 'Effective From is required; ';
            }
            
            if (!effectiveToDate) {
                hasError = true;
                newDateErrors.effectiveTo = true;
                errorMessage += 'Effective To is required; ';
            } else if (isActivePolicyEffectiveToBeforeToday(activePolicy, effectiveToDate)) {
                hasError = true;
                newDateErrors.effectiveTo = true;
                errorMessage += `${ACTIVE_POLICY_EFFECTIVE_TO_ERROR_MESSAGE}; `;
            }

            const deductibleTotal = parseFloat(formData.deductible);
            const deductibleRemaining = parseFloat(formData.remaining);
            const oopmTotal = parseFloat(formData.oopm);
            const oopmRemaining = parseFloat(formData.oopmRemaining);
            const copay = parseFloat(formData.copay);
            const maxDays = parseFloat(formData.maxDays);
            const coinsurance = parseFloat(formData.coinsurance);

            let hasNegative = false;
            let hasValidationError = false;

            if (!isNaN(deductibleTotal) && formData.deductible !== '' && deductibleTotal < 0) {
                setDeductibleError(true);
                setDeductibleErrorMessage('Total cannot be negative');
                hasNegative = true;
            } else {
                setDeductibleError(false);
                setDeductibleErrorMessage('');
            }
            if (!isNaN(deductibleRemaining) && formData.remaining !== '' && deductibleRemaining < 0) {
                setRemainingError(true);
                setRemainingErrorMessage('Remaining cannot be negative');
                hasNegative = true;
            } else {
                setRemainingError(false);
                setRemainingErrorMessage('');
            }
            if (!isNaN(oopmTotal) && formData.oopm !== '' && oopmTotal < 0) {
                setOopmError(true);
                setOopmErrorMessage('Total cannot be negative');
                hasNegative = true;
            } else {
                setOopmError(false);
                setOopmErrorMessage('');
            }
            if (!isNaN(oopmRemaining) && formData.oopmRemaining !== '' && oopmRemaining < 0) {
                setOopmRemainingError(true);
                setOopmRemainingErrorMessage('Remaining cannot be negative');
                hasNegative = true;
            } else {
                setOopmRemainingError(false);
                setOopmRemainingErrorMessage('');
            }
            if (!isNaN(copay) && formData.copay !== '' && copay < 0) {
                setCopayError(true);
                setCopayErrorMessage('Co-pay cannot be negative');
                hasNegative = true;
            } else {
                setCopayError(false);
                setCopayErrorMessage('');
            }
            if (!isNaN(maxDays) && formData.maxDays !== '' && maxDays < 0) {
                setMaxDaysError(true);
                setMaxDaysErrorMessage('Max Days cannot be negative');
                hasNegative = true;
            } else {
                setMaxDaysError(false);
                setMaxDaysErrorMessage('');
            }
            if (!isNaN(coinsurance) && formData.coinsurance !== '' && coinsurance < 0) {
                setCoinsuranceError(true);
                setCoinsuranceErrorMessage('Coinsurance cannot be negative');
                hasNegative = true;
            } else if (!isNaN(coinsurance) && formData.coinsurance !== '' && coinsurance > 100) {
                setCoinsuranceError(true);
                setCoinsuranceErrorMessage('Patient % cannot be greater than 100');
                hasValidationError = true;
            } else {
                setCoinsuranceError(false);
                setCoinsuranceErrorMessage('');
            }

            if (hasNegative) {
                setDateErrors(newDateErrors);
                setIsSaving(false);
                dispatch(showErrorStatus('Please correct negative values'));
                return;
            }

            if (hasValidationError) {
                setDateErrors(newDateErrors);
                setIsSaving(false);
                dispatch(showErrorStatus('Please correct validation errors'));
                return;
            }

            if (!isNaN(deductibleTotal) && !isNaN(deductibleRemaining) &&
                formData.deductible !== '' && formData.remaining !== '' &&
                deductibleTotal < deductibleRemaining) {
                hasError = true;
                setDeductibleError(true);
                setRemainingError(true);
                setDeductibleErrorMessage('Deductible Total must be greater than or equal to Remaining');
                setRemainingErrorMessage('Deductible Total must be greater than or equal to Remaining');
                errorMessage += 'Deductible Total must be greater than or equal to Remaining; ';
            } else {
                setDeductibleErrorMessage('');
                setRemainingErrorMessage('');
            }
            if (!isNaN(oopmTotal) && !isNaN(oopmRemaining) &&
                formData.oopm !== '' && formData.oopmRemaining !== '' &&
                oopmTotal < oopmRemaining) {
                hasError = true;
                setOopmError(true);
                setOopmRemainingError(true);
                setOopmErrorMessage('Out of Pocket Maximum Total must be greater than or equal to Remaining');
                setOopmRemainingErrorMessage('Out of Pocket Maximum Total must be greater than or equal to Remaining');
                errorMessage += 'Out of Pocket Maximum Total must be greater than or equal to Remaining; ';
            } else {
                setOopmErrorMessage('');
                setOopmRemainingErrorMessage('');
            }

            if (!hasError) {
                if (formData.deductible && !formData.remaining) {
                    hasError = true;
                    setDeductibleError(false);
                    setRemainingError(true);
                    setRemainingErrorMessage('Remaining is required when Deductible is filled');
                    errorMessage += 'Remaining is required when Deductible is filled; ';
                } else if (!formData.deductible && formData.remaining) {
                    hasError = true;
                    setDeductibleError(true);
                    setRemainingError(false);
                    setDeductibleErrorMessage('Deductible is required when Remaining is filled');
                    errorMessage += 'Deductible is required when Remaining is filled; ';
                } else {
                    setDeductibleError(false);
                    setRemainingError(false);
                    setDeductibleErrorMessage('');
                    setRemainingErrorMessage('');
                }
            }
            if (!hasError) {
                if (formData.oopm && !formData.oopmRemaining) {
                    hasError = true;
                    setOopmError(false);
                    setOopmRemainingError(true);
                    setOopmRemainingErrorMessage('OOP Remaining is required when OOPM is filled');
                    errorMessage += 'OOP Remaining is required when OOPM is filled; ';
                } else if (!formData.oopm && formData.oopmRemaining) {
                    hasError = true;
                    setOopmError(true);
                    setOopmRemainingError(false);
                    setOopmErrorMessage('OOPM is required when OOP Remaining is filled');
                    errorMessage += 'OOPM is required when OOP Remaining is filled; ';
                } else {
                    setOopmError(false);
                    setOopmRemainingError(false);
                    setOopmErrorMessage('');
                    setOopmRemainingErrorMessage('');
                }
            }

            const newLevelErrors = new Map();
            let hasLevelErrors = false;
            
            if (levelsOfCare && levelsOfCare.length > 0) {
                levelsOfCare.forEach(level => {
                    const vobClassificationId = level.vobClassificationId;
                    if (!vobClassificationId) return;
                    
                    const combinationKey = `${networkType}_${memberType}`;
                    const levelData = savedLOCDataMap.get(vobClassificationId);
                    const savedData = levelData?.get(combinationKey) || {};
                    
                    const levelError: any = {};

                    const deductibleTotal = parseFloat(savedData.total || '');
                    const deductibleRemaining = parseFloat(savedData.remaining || '');
                    
                    if (!isNaN(deductibleTotal) && savedData.total !== '' && deductibleTotal < 0) {
                        levelError.deductibleTotalError = true;
                        levelError.deductibleTotalErrorMessage = 'Total cannot be negative';
                        hasLevelErrors = true;
                    }
                    if (!isNaN(deductibleRemaining) && savedData.remaining !== '' && deductibleRemaining < 0) {
                        levelError.deductibleRemainingError = true;
                        levelError.deductibleRemainingErrorMessage = 'Remaining cannot be negative';
                        hasLevelErrors = true;
                    }
                    if (!isNaN(deductibleTotal) && !isNaN(deductibleRemaining) &&
                        savedData.total !== '' && savedData.remaining !== '' &&
                        deductibleTotal < deductibleRemaining) {
                        levelError.deductibleTotalError = true;
                        levelError.deductibleRemainingError = true;
                        levelError.deductibleTotalErrorMessage = 'Deductible Total must be greater than or equal to Remaining';
                        levelError.deductibleRemainingErrorMessage = 'Deductible Total must be greater than or equal to Remaining';
                        hasLevelErrors = true;
                    }
                    if (savedData.total && !savedData.remaining) {
                        levelError.deductibleRemainingError = true;
                        levelError.deductibleRemainingErrorMessage = 'Remaining is required when Deductible is filled';
                        hasLevelErrors = true;
                    } else if (!savedData.total && savedData.remaining) {
                        levelError.deductibleTotalError = true;
                        levelError.deductibleTotalErrorMessage = 'Deductible is required when Remaining is filled';
                        hasLevelErrors = true;
                    }

                    const oopmTotal = parseFloat(savedData.oopmTotal || '');
                    const oopmRemaining = parseFloat(savedData.oopmRemaining || '');
                    
                    if (!isNaN(oopmTotal) && savedData.oopmTotal !== '' && oopmTotal < 0) {
                        levelError.oopmTotalError = true;
                        levelError.oopmTotalErrorMessage = 'Total cannot be negative';
                        hasLevelErrors = true;
                    }
                    if (!isNaN(oopmRemaining) && savedData.oopmRemaining !== '' && oopmRemaining < 0) {
                        levelError.oopmRemainingError = true;
                        levelError.oopmRemainingErrorMessage = 'Remaining cannot be negative';
                        hasLevelErrors = true;
                    }
                    if (!isNaN(oopmTotal) && !isNaN(oopmRemaining) &&
                        savedData.oopmTotal !== '' && savedData.oopmRemaining !== '' &&
                        oopmTotal < oopmRemaining) {
                        levelError.oopmTotalError = true;
                        levelError.oopmRemainingError = true;
                        levelError.oopmTotalErrorMessage = 'Out of Pocket Maximum Total must be greater than or equal to Remaining';
                        levelError.oopmRemainingErrorMessage = 'Out of Pocket Maximum Total must be greater than or equal to Remaining';
                        hasLevelErrors = true;
                    }
                    if (savedData.oopmTotal && !savedData.oopmRemaining) {
                        levelError.oopmRemainingError = true;
                        levelError.oopmRemainingErrorMessage = 'OOP Remaining is required when OOPM is filled';
                        hasLevelErrors = true;
                    } else if (!savedData.oopmTotal && savedData.oopmRemaining) {
                        levelError.oopmTotalError = true;
                        levelError.oopmTotalErrorMessage = 'OOPM is required when OOP Remaining is filled';
                        hasLevelErrors = true;
                    }

                    const copay = parseFloat(savedData.perDayVisit || '');
                    const maxDays = parseFloat(savedData.maxDays || '');
                    
                    if (!isNaN(copay) && savedData.perDayVisit !== '' && copay < 0) {
                        levelError.copayError = true;
                        levelError.copayErrorMessage = 'Co-pay cannot be negative';
                        hasLevelErrors = true;
                    }
                    if (!isNaN(maxDays) && savedData.maxDays !== '' && maxDays < 0) {
                        levelError.maxDaysError = true;
                        levelError.maxDaysErrorMessage = 'Max Days cannot be negative';
                        hasLevelErrors = true;
                    }
                    

                    const coinsurance = parseFloat(savedData.patient || '');
                    
                    if (!isNaN(coinsurance) && savedData.patient !== '' && coinsurance < 0) {
                        levelError.coinsuranceError = true;
                        levelError.coinsuranceErrorMessage = 'Coinsurance cannot be negative';
                        hasLevelErrors = true;
                    }
                    
                    if (!isNaN(coinsurance) && savedData.patient !== '' && coinsurance > 100) {
                        levelError.coinsuranceError = true;
                        levelError.coinsuranceErrorMessage = 'Patient % cannot be greater than 100';
                        hasLevelErrors = true;
                    }
                    
                    if (Object.keys(levelError).length > 0) {
                        newLevelErrors.set(vobClassificationId, levelError);
                    }
                });
            }
            
            setLevelErrors(newLevelErrors);
            setDateErrors(newDateErrors);
            
            if (hasError || hasLevelErrors) {
                const finalErrorMessage = hasLevelErrors ? 
                    `${errorMessage} Please check Levels of Care for validation errors.` :
                    errorMessage;
                dispatch(showErrorStatus(finalErrorMessage));
                setIsSaving(false);
                return;
            }
            
            try {
                const saveSuccess = await saveVOB();
                if (saveSuccess) {
                    const savedVob = saveSuccess as Vob;
                    const facilityId = Number(
                        savedVob.facility?.facilityId ?? selectedFacility
                    );
                    const encounterId =
                        estimate?.patientEncounterId ??
                        selectedEncounter?.patientEncounterId;

                    const syncResult = await syncIocFacilityAfterVobSave(
                        dispatch,
                        Number(encounterId),
                        facilityId
                    );
                    if (!syncResult.success) {
                        dispatch(
                            showErrorStatus(
                                syncResult.errorMessage ||
                                    'VOB saved, but IOC facility sync failed'
                            )
                        );
                        return;
                    }

                    if (estimate && estimate.estimateId) {
                        try {
                            const newVobId = savedVob.vobId ?? currentVob?.vobId!;
                            const changingFromFacility = (currentVob?.facility.facilityId !== selectedFacility);
                            // if we had changes to the facility or payor we need to run loadandset
                            if(changingFromFacility) {
                                const clientId = Number(selectedPatient.clientId);
                                const clientObj = { clientId } as Client;
                                const currentFacility = { facilityId: Number(selectedFacility) };
                                let updatedEstimate: Estimate = {
                                    ...Utils.deepClone(estimate),
                                    facilityId: selectedFacility,
                                };
                                const savedRateAs = (savedVob as Vob & { rateAsPayorPlan?: PayorPlan })?.rateAsPayorPlan;
                                const selectedPayor: Payor = {
                                    payorId: savedRateAs?.payor?.payorId
                                        ?? currentVob?.payor?.payorId
                                        ?? selectedPatientCurrentInsurance?.payorId
                                };
                                const selectedPlan: Plan = {
                                    payorPlanId: savedRateAs?.plan?.payorPlanId
                                        ?? currentVob?.plan?.payorPlanId
                                        ?? selectedPatientCurrentInsurance?.payorPlanId,
                                    ...(savedRateAs?.plan?.facilityPlanId != null && {
                                        facilityPlanId: savedRateAs.plan.facilityPlanId,
                                    }),
                                };
                                await loadAndSetRateAsPayorPlan(
                                    clientObj,
                                    currentFacility,
                                    dispatch,
                                    reduxMasterPayors,
                                    selectedPayor,
                                    selectedPlan,
                                    reduxRateAsPayorMap,
                                    estimate!,
                                    rateAsPayorPlan,
                                    savedVob,
                                    updatedEstimate!,
                                    newVobId!,
                                    true
                                );
                            } else {
                              // NO facility changes: still rematch Estimate LOCs when Rate as changed
                                // When vob.rateAsPayorPlan.isOverridden is true, use vob's Rate as for estimate; else fall back to Redux rateAsPayorPlan
                                const rateAsPayorPlanForEstimate = ((): PayorPlan | null | undefined => {
                                    if (selectedRateAsFacilityPlanId != null && facilityPayersState?.length) {
                                        const payer = facilityPayersState.find((p: any) =>
                                            p.facilityPayorPlans?.some((pl: { facilityPayorPlanId: number }) => Number(pl.facilityPayorPlanId) === Number(selectedRateAsFacilityPlanId))
                                        );
                                        const plan = payer?.facilityPayorPlans?.find((pl: { facilityPayorPlanId: number }) => Number(pl.facilityPayorPlanId) === Number(selectedRateAsFacilityPlanId));
                                        if (payer && plan) {
                                            const payorName = payer.facilityPayorName || reduxMasterPayors?.get(payer.cfgPayorId)?.payorName;
                                            const planName = plan.facilityPayorPlanName || reduxMasterPayors?.get(payer.cfgPayorId)?.payorPlans?.find((p: any) => Number(p.payorPlanId) === Number(plan.cfgPayorPlanId))?.planName;
                                            return {
                                                payor: { payorId: payer.cfgPayorId, facilityPayorName: payer.facilityPayorName, payorName, inNetwork: payer.inNetwork } as Payor,
                                                plan: { payorPlanId: plan.cfgPayorPlanId, facilityPlanId: plan.facilityPayorPlanId, facilityPlanName: plan.facilityPayorPlanName, planName } as Plan,
                                                isOverridden: true,
                                            } as PayorPlan;
                                        }
                                    }
                                    const savedVobRateAs = (savedVob as Vob & { rateAsPayorPlan?: PayorPlan })?.rateAsPayorPlan;
                                    if (savedVobRateAs?.isOverridden === true && savedVobRateAs?.payor && savedVobRateAs?.plan) {
                                        return savedVobRateAs;
                                    }
                                    const vobRateAs = (currentVob as Vob & { rateAsPayorPlan?: PayorPlan })?.rateAsPayorPlan;
                                    if (vobRateAs?.isOverridden === true && vobRateAs?.payor && vobRateAs?.plan) {
                                        return vobRateAs;
                                    }
                                    if (rateAsPayorPlan?.payor && rateAsPayorPlan?.plan) {
                                        return {
                                            ...rateAsPayorPlan,
                                            isOverridden:
                                                selectedRateAsFacilityPlanId != null ||
                                                rateAsPayorPlan.isOverridden === true,
                                        } as PayorPlan;
                                    }
                                    return undefined;
                                })();

                                const previousRateAs = estimate?.rateAsPayorPlan as PayorPlan | undefined;
                                const rateAsChanged = (() => {
                                    if (!rateAsPayorPlanForEstimate?.payor || !rateAsPayorPlanForEstimate?.plan) {
                                        return false;
                                    }
                                    if (!previousRateAs?.payor || !previousRateAs?.plan) {
                                        return true;
                                    }
                                    const prevFacilityPlanId = previousRateAs.plan.facilityPlanId;
                                    const nextFacilityPlanId = rateAsPayorPlanForEstimate.plan.facilityPlanId;
                                    if (prevFacilityPlanId != null && nextFacilityPlanId != null) {
                                        return Number(prevFacilityPlanId) !== Number(nextFacilityPlanId);
                                    }
                                    return (
                                        Number(previousRateAs.payor.payorId) !== Number(rateAsPayorPlanForEstimate.payor.payorId) ||
                                        Number(previousRateAs.plan.payorPlanId) !== Number(rateAsPayorPlanForEstimate.plan.payorPlanId)
                                    );
                                })();

                                const putEstimateBody = {
                                    ...estimate,
                                    patientId: selectedPatient.patientId,
                                    vobId: newVobId,
                                    ...(rateAsPayorPlanForEstimate && { rateAsPayorPlan: rateAsPayorPlanForEstimate }),
                                    isSelfPay: isSelfPay,
                                    isPrivatePay: isPrivatePay,
                                    description: estimate?.description || ''
                                };

                                if (rateAsChanged && rateAsPayorPlanForEstimate) {
                                    const clientObj: Client = savedVob.client ?? currentVob?.client ?? { clientId: Number(selectedPatient.clientId) } as Client;
                                    const facilityObj: Facility = savedVob.facility ?? currentVob?.facility ?? { facilityId: Number(selectedFacility) } as Facility;
                                    const updatedEstimate: Estimate = {
                                        ...estimate,
                                        ...putEstimateBody,
                                        rateAsPayorPlan: rateAsPayorPlanForEstimate,
                                    };
                                    await loadAndSetRateAsPayorPlan(
                                        clientObj,
                                        facilityObj,
                                        dispatch,
                                        reduxMasterPayors,
                                        rateAsPayorPlanForEstimate.payor,
                                        rateAsPayorPlanForEstimate.plan,
                                        reduxRateAsPayorMap,
                                        estimate!,
                                        rateAsPayorPlan,
                                        undefined,
                                        updatedEstimate,
                                        newVobId!,
                                        true
                                    );
                                    if (!estimate?.facilityLevelOfCare?.length) {
                                        await dispatch(saveEstimateAfterVob({
                                            estimateId: estimate?.estimateId,
                                            estimate: putEstimateBody,
                                            isSelfPay: isSelfPay,
                                            isPrivatePay: isPrivatePay
                                        }));
                                    }
                                } else {
                                    await dispatch(saveEstimateAfterVob({
                                        estimateId: estimate?.estimateId,
                                        estimate: putEstimateBody,
                                        isSelfPay: isSelfPay,
                                        isPrivatePay: isPrivatePay
                                    }));
                                }
                                }
                            
                            dispatch(showStatus('VOB, Estimate, and IOC saved successfully'));
                        } catch (estimateError) {
                            console.error('Error saving estimate after VOB:', estimateError);
                            dispatch(showStatus('VOB and IOC saved, but estimate save failed'));
                        }
                    } else {
                        dispatch(showStatus('VOB and IOC saved successfully'));
                    }
                }
            } catch (error) {
                dispatch(showStatus('VOB saved failed'));
            }
        } finally {
            setIsSaving(false);
        }
    };


    const handleDownloadPdf = () => {
        if (currentVob?.vobId) {
            dispatch(downloadVobPdf(currentVob.vobId));
        }
    };

    return (
        <Box test-id="vob-form-container">
            <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Grid item xs sx={{ minWidth: 0 }}>
                    <VOBSelector
                        selectedVOB={selectedVOB}
                        onVOBChange={handleVOBSelectChange}
                        rateAsPayorPlan={rateAsPayorPlan}
                        facilities={facilities}
                    />
                </Grid>
                <Grid item sx={{ 
                    minWidth: '200px',
                    flexShrink: 0,
                    flexGrow: 0,
                    width: 'auto',
                    paddingTop: "0px !important"
                }}>
                    <Box
                        ref={vobHeaderActionsRef}
                        test-id="vob-form-actions"
                        sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'nowrap',
                            minWidth: 'fit-content',
                            width: 'fit-content',
                        }}
                    >
                        <FPButton
                            test-id="vob-new-button"
                            onClick={async () => {
                                if (selectedPatient?.patientId) {
                                    const now = new Date();
                                    const year = now.getFullYear();
                                    setActivePolicy(true);
                                    setCarryover(false);
                                    setLiveVOB(false);
                                    handleEffectiveFromDateChange(new Date(`${year}-01-01`));
                                    handleEffectiveToDateChange(new Date(`${year}-12-31`));
                                    handleInputChange('deductible', '');
                                    handleInputChange('remaining', '');
                                    handleInputChange('oopm', '');
                                    handleInputChange('oopmRemaining', '');
                                    handleInputChange('copay', '');
                                    handleInputChange('maxDays', '');
                                    handleInputChange('coinsurance', '');
                                    handleInputChange('notes', '');
                                    handleInputChange('isDedIncOOPM', true);
                                    handleVOBSelectChange('');
                                    setResetButtonStates(true);
                                    setSavedFormDataMap(new Map());
                                    setSavedLOCDataMap(new Map());
                                    dispatch(clearSelectedEstimate());
                                    navigate(`/patients/${selectedPatient.patientId}/vob`);
                                    const newVobFacilityId = resolveDefaultFacilityId(
                                        facilities,
                                        selectedEncounter?.facilityId
                                    );
                                    if (newVobFacilityId != null) {
                                        setSelectedFacility(newVobFacilityId);
                                    }
                                    if (newVobFacilityId && selectedPatient?.clientId) {
                                        const client = { clientId: Number(selectedPatient.clientId) } as Client;
                                        const facility = { facilityId: Number(newVobFacilityId) };
                                        const payor = {
                                            payorId: selectedPatientCurrentInsurance?.payorId
                                        } as Payor;
                                        const plan = {
                                            payorPlanId: selectedPatientCurrentInsurance?.payorPlanId
                                        } as Plan;
                                        await loadAndSetRateAsPayorPlan(
                                            client,
                                            facility,
                                            dispatch,
                                            reduxMasterPayors,
                                            payor,
                                            plan,
                                            reduxRateAsPayorMap
                                        );
                                    }
                                }
                            }}
                            style={{ 
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                            }}
                        >
                            New
                        </FPButton>
                        <FPButton
                            test-id="vob-save-button"
                            onClick={handleSaveVOB}
                            disabled={isSaving}
                            style={{ 
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                            }}
                            // startIcon={isSaving ? <CircularProgress size={20} /> : null}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </FPButton>
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 0.5 }} alignItems="center">
                <Grid item xs={4}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="facility-select-label">
                            {facilitiesLoading || (!selectedFacility && facilities.length === 0) ? 'Loading...' : 'Facility'}
                        </InputLabel>
                        <Select
                            test-id="vob-facility-select"
                            labelId="facility-select-label"
                            id="facility-select"
                            value={selectedFacility}
                            label={facilitiesLoading || (!selectedFacility && facilities.length === 0) ? 'Loading...' : 'Facility'}
                            onChange={(e) => handleFacilityChange(Number(e.target.value))}
                            error={facilityError}
                            disabled={facilitiesLoading || (!selectedFacility && facilities.length === 0)}
                            className={`state-field ${facilityError ? 'error-border' : 'required-fields'}`}
                        >
                            {facilitiesLoading || (!selectedFacility && facilities.length === 0) ? (
                                <MenuItem disabled>Loading facilities...</MenuItem>
                            ) : (
                                facilities.map((facility) => (
                                    <MenuItem key={facility.facilityId} value={facility.facilityId}>
                                        {facility.facilityName}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid
                    item
                    xs={8}
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                    }}
                >
                    {isLoadingRunAvailityVOBButton ? (
                        <FPButton
                            onClick={runAvailityVob}
                            disabled={isRunningAvailityVOB}
                            test-id="run-availability-vob"
                            style={{
                                ...(headerActionsWidthPx > 0
                                    ? {
                                          width: headerActionsWidthPx,
                                          boxSizing: 'border-box',
                                      }
                                    : {}),
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                            }}
                        >
                            Run Availity VOB
                        </FPButton>
                    ) : null}
                </Grid>
            </Grid>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    mb: 3,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            flex: '1 1 auto',
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            component="div"
                            test-id="vob-payer-display"
                            variant="body1"
                            sx={{
                                mr: 2,
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                columnGap: 0.5,
                                rowGap: 0.25,
                                lineHeight: 1.5,
                            }}
                        >
                            <Box component="span">
                                Payer:{' '}
                                {payerPortalUrl && payerNameText ? (
                                    <Link
                                        test-id="vob-payer-portal-link"
                                        href={payerPortalUrl}
                                        target="payorPortal"
                                        rel="noopener noreferrer"
                                    >
                                        {payerNameText}
                                    </Link>
                                ) : (
                                    payerNameText
                                )}
                                {payerPlanText}
                                {payerLineInsuranceSuffix}
                                {payerLineNetworkIdSuffix}
                            </Box>
                            {payerPhoneDisplay ? (
                                <Box
                                    component="span"
                                    test-id="vob-payer-phone"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                >
                                    <PhoneIcon sx={{ fontSize: '1.5rem', display: 'block' }} aria-hidden />
                                    {payerPhoneDisplay}
                                </Box>
                            ) : null}
                        </Typography>
                        {comparisonError && (
                            <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                ({comparisonError})
                            </Typography>
                        )}
                    </Box>
                    <FormControlLabel
                        test-id="vob-live-vob-checkbox"
                        sx={{ flexShrink: 0, marginLeft: 'auto', mb: 0, mt: 0 }}
                        control={
                            <Checkbox
                                test-id="vob-live-vob-checkbox-input"
                                checked={liveVOB}
                                onChange={(e) => setLiveVOB(e.target.checked)}
                            />
                        }
                        label="Live VOB"
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        columnGap: 2,
                        rowGap: 0.5,
                        /* Pull toward payer row: body1 line-height leaves ~extra below baseline; small mt was invisible */
                        mt: -0.75,
                    }}
                >
                    {planSponsorDisplayLine ? (
                        <Typography
                            variant="body1"
                            component="span"
                            test-id="vob-sponsor-display"
                            sx={{ lineHeight: 1.5 }}
                        >
                            {planSponsorDisplayLine}
                        </Typography>
                    ) : null}
                    {vobGroupDisplayLine ? (
                        <Typography
                            variant="body1"
                            component="span"
                            test-id="vob-group-display"
                            sx={{ lineHeight: 1.5 }}
                        >
                            {vobGroupDisplayLine}
                        </Typography>
                    ) : null}
                    {employerDisplayLine ? (
                        <Typography
                            variant="body1"
                            component="span"
                            test-id="vob-employer-display"
                            sx={{ lineHeight: 1.5 }}
                        >
                            {employerDisplayLine}
                        </Typography>
                    ) : null}
                    <Typography
                        variant="body1"
                        component="span"
                        test-id="vob-member-id-display"
                        sx={{ lineHeight: 1.5 }}
                    >
                        Member ID: {memberId}
                    </Typography>
                </Box>

                {subscriberDisplayLine ? (
                    <Typography
                        variant="body1"
                        component="div"
                        test-id="vob-subscriber-display"
                        sx={{ mt: 0.5, lineHeight: 1.5 }}
                    >
                        {subscriberDisplayLine}
                    </Typography>
                ) : null}
            </Box>

            <RateAsSelector
                rateAsPayorPlan={rateAsPayorPlan}
                vobRateAsPayorPlan={vobRateAsPayorPlan}
                facilityPayersState={facilityPayersState}
                payersMap={payersMap}
                selectedFacilityPlanId={selectedRateAsFacilityPlanId ?? undefined}
                selectedFacility={selectedFacility}
                vobFacilityId={currentVob?.facility?.facilityId != null ? Number(currentVob.facility.facilityId) : undefined}
                onRateAsPayorPlanChange={handleRateAsPayorPlanChangeTabOnly}
            />

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                    <DatePicker
                        test-id="vob-effective-from-date"
                        label="Effective From"
                        value={effectiveFromDate}
                        onChange={handleEffectiveFromDateChange}
                        error={dateErrors.effectiveFrom}
                        className={`state-field ${dateErrors.effectiveFrom ? 'error-border' : 'required-fields'}`}
                    />
                </Grid>
                <Grid item xs={3}>
                    <DatePicker
                        test-id="vob-effective-to-date"
                        label="Effective To"
                        value={effectiveToDate}
                        onChange={handleEffectiveToDateChange}
                        error={dateErrors.effectiveTo}
                        className={`state-field ${dateErrors.effectiveTo ? 'error-border' : 'required-fields'}`}
                    />
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        test-id="vob-active-policy-checkbox"
                        control={
                            <Checkbox
                                test-id="vob-active-policy-checkbox-input"
                                checked={activePolicy}
                                onChange={(e) => setActivePolicy(e.target.checked)}
                            />
                        }
                        label="Active Policy"
                    />
                    <FormControlLabel
                        test-id="vob-carryover-checkbox"
                        control={
                            <Checkbox
                                test-id="vob-carryover-checkbox-input"
                                checked={carryover}
                                onChange={(e) => setCarryover(e.target.checked)}
                            />
                        }
                        label="4th Quarter Carryover"
                    />
                </Grid>
            </Grid>

            <NetworkTypeSelector
                networkType={networkType}
                memberType={memberType}
                onNetworkTypeChange={handleNetworkTypeChange}
                onMemberTypeChange={handleMemberTypeChange}
            />

            <VOBFormFields
                formData={formData}
                onInputChange={handleInputChange}
                currentVob={currentVob}
                deductibleError={deductibleError}
                remainingError={remainingError}
                oopmError={oopmError}
                oopmRemainingError={oopmRemainingError}
                deductibleErrorMessage={deductibleErrorMessage}
                remainingErrorMessage={remainingErrorMessage}
                oopmErrorMessage={oopmErrorMessage}
                oopmRemainingErrorMessage={oopmRemainingErrorMessage}
                copayError={copayError}
                maxDaysError={maxDaysError}
                coinsuranceError={coinsuranceError}
                copayErrorMessage={copayErrorMessage}
                maxDaysErrorMessage={maxDaysErrorMessage}
                coinsuranceErrorMessage={coinsuranceErrorMessage}
                resetButtonStates={resetButtonStates}
                setButtonStates={buttonStates}
            />

            <LevelsOfCare
                levels={levelsOfCare}                
                networkType={networkType}
                memberType={memberType}
                setSavedLOCDataMap={setSavedLOCDataMap}
                savedLOCDataMap={savedLOCDataMap}
                savedFormDataMap={savedFormDataMap}
                levelErrors={levelErrors}
            />

            <Box test-id="vob-form-bottom-actions" sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <AccessControl 
                    rolePageName={RolePageNames.Estimates} 
                    actionName="PDF Download"
                    renderNoAccess={() => null}
                >
                    <Button 
                        test-id="vob-download-pdf-button"
                        variant="contained"
                        color="primary"
                        style={{marginRight: "6px"}}
                        onClick={handleDownloadPdf}
                        disabled={!currentVob?.vobId || isSaving}
                        startIcon={<PictureAsPdfIcon />}
                    >
                        Download PDF
                    </Button>
                </AccessControl>
                <FPButton 
                    test-id="vob-save-button-bottom"
                    onClick={handleSaveVOB}
                    disabled={isSaving}
                    // startIcon={isSaving ? <CircularProgress size={20} /> : null}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </FPButton>
            </Box>

            <AvailityErrorDialog
                open={showAvailityErrorDialog}
                onClose={() => setShowAvailityErrorDialog(false)}
                availityErrorMessage={availityErrorMessage}
                selectedPatient={selectedPatient}
                selectedPatientCurrentInsurance={selectedPatientCurrentInsurance}
                isRunningAvailityVOB={isRunningAvailityVOB}
                onRunAvailityVob={runAvailityVobWithOverrides}
            />
        </Box>
    );
}

export default VOBForm;